import React, { useContext, useState } from "react";

import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartContainer from './smartForm/SmartContainer';
import SmartDateTimeInput from "./smartForm/SmartDateTimeInput";
import SmartTextareaInput from "./smartForm/SmartTextareaInput";

import { useChallengeClubCreateMutation, useChallengeSiteCreateMutation } from "@mutation/challengeMutation";
import { AlertContext } from "@context/AlertProvider";
import { useTranslation } from "react-i18next";
import { removeEmptyValues } from '@utils/generalUtils';

import { ClubSiteTargetObject, SelectClubSiteTarget } from "@component/SelectClubSiteTarget";
import { SelectChallengeType } from "./SelectChallengeType";

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// Utils and Services
import { 
  createNumberMinValidator, 
  createLengthInRangeValidator, 
  createLengthMaxValidator,
  createDateBeforeValidator,
  createDateAfterValidator,
  notEmptyValidator 
} from "@component/smartForm/fieldValidators";

function ChallengeCreateForm({ onSave, onCancel }) {
    // Validators
    const nameValidator = createLengthInRangeValidator(3, 50);
    const descriptionLengthValidator = createLengthMaxValidator(500);
    const initBankValidator = createNumberMinValidator(0);

    const challengeClubCreateMutation = useChallengeClubCreateMutation();
    const challengeSiteCreateMutation = useChallengeSiteCreateMutation();

    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(["main", "challenge"]);

    const [challengeModel, setChallengeModel] = useState(undefined);
    const [target, setTarget] = useState(new ClubSiteTargetObject(null, null));
    const [type, setType] = useState('');

    const validateDescription = (value) => {
        if (value == null || value === "") return "";
        return descriptionLengthValidator(value);
    };

    const startBeforeEndValidator = createDateBeforeValidator(() => challengeModel?.end);
    const endAfterStartValidator = createDateAfterValidator(() => challengeModel?.start);

    const validateStart = (value) => {
        const emptyError = notEmptyValidator(value);
        if (emptyError) return emptyError;
        return startBeforeEndValidator(value);
    };

    const validateEnd = (value) => {
        const emptyError = notEmptyValidator(value);
        if (emptyError) return emptyError;
        return endAfterStartValidator(value);
    };

    const handleSave = (newModel) => {
        const cleaned = removeEmptyValues({
            name: newModel.name,
            description: newModel.description,
            initial_bank: newModel.initial_bank,
            start: newModel.start,
            end: newModel.end,
            type_uuid: type,
            auto_join: true,
        });

        const handleMutationError = (error) => {
            if (!error.response) {
            addAlertBanner(t("error:networkError"), "error", 4000);
            return;
            }

            const statusCode = error.response.status;
            const errorDetail = error.response.data?.detail;

            if (statusCode === 403) {
            addAlertBanner(
                target.club_uuid 
                ? t("error:noClubCreationPrivileges") 
                : t("error:noSiteCreationPrivileges"),
                "error",
                5000
            );
            } else if (statusCode === 400) {
            addAlertBanner(
                errorDetail || t("error:creationValidationError"),
                "error",
                5000
            );
            } else if (statusCode === 404) {
            addAlertBanner(
                target.club_uuid
                ? t("error:clubNotFound")
                : t("error:siteNotFound"),
                "error",
                4000
            );
            } else if (statusCode >= 500) {
            addAlertBanner(t("error:serverError"), "error", 4000);
            } else {
            addAlertBanner(t("error:somethingGoesWrong"), "error", 4000);
            }
        };

        // Create new challenge
        if (target.club_uuid) {
            challengeClubCreateMutation.mutate(
            { data: { ...cleaned, club_uuid: target.club_uuid }},
            {
                onSuccess: () => {
                addAlertBanner(t("main:saveSuccess"), "success", 4000);
                onSave?.();
                },
                onError: handleMutationError
            }
            );
        } else if (target.site_uuid) {
            challengeSiteCreateMutation.mutate(
            { data: { ...cleaned, site_uuid: target.site_uuid }},
            {
                onSuccess: () => {
                addAlertBanner(t("main:saveSuccess"), "success", 4000);
                onSave?.();
                },
                onError: handleMutationError
            }
            );
        } else {
            addAlertBanner(t("error:selectTargetError"), "error", 4000);
        }
    };

    const handleCancel = () => {
        setChallengeModel(undefined);
        setTarget(new ClubSiteTargetObject(null, null));
        onCancel?.();
    };

    return (
        <SmartForm
            formModel={challengeModel}
            setFormModel={setChallengeModel}
            onSave={handleSave}
            onCancel={handleCancel}
            isCreateOnly={true}
            buttonPanelSx={{
                position: "initial",
                p: 1,
                flexDirection: "row-reverse"
            }}
        >
            {/* Name and Description Section */}
            <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <SmartTextInput 
                field="name" 
                label={t("challenges:name")}
                validator={nameValidator}
                />
                <SmartTextareaInput 
                field="description" 
                label={t("challenges:description")} 
                rows={3} 
                validator={validateDescription}
                />
            </SmartContainer>

            {/* Date Section */}
            <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <SmartDateTimeInput 
                sx={{ flex: 1 }} 
                startIcon={<CalendarMonthIcon />} 
                field="start" 
                label={t("challenges:start")} 
                validator={validateStart}
                ampm={false}
                />
                <SmartDateTimeInput 
                sx={{ flex: 1 }} 
                startIcon={<CalendarMonthIcon />} 
                field="end" 
                label={t("challenges:end")} 
                validator={validateEnd}
                ampm={false}
                />
            </SmartContainer>

            {/* Bank and Hosting Section */}
            <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <SmartTextInput 
                field="initial_bank" 
                label={t("challenges:initialBank")}
                sx={{ flex: 1 }}
                validator={initBankValidator}
                />

                <SelectClubSiteTarget 
                    target={target} 
                    setTarget={setTarget} 
                    sx={{ flex: 1 }}
                />
            </SmartContainer>

            {/* Challenge Type Section */}
            <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                <SelectChallengeType 
                    target={type} 
                    setTarget={setType} 
                    sx={{ flex: 1 }}
                />
                
            </SmartContainer>
        </SmartForm>
    );
}

export default ChallengeCreateForm;
